import {
  ChevronRight,
  CloudOff,
  Clock3,
  Droplet,
  FlaskConical,
  LoaderCircle,
  Package,
  Thermometer,
  TriangleAlert,
  Wind,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { fmt1, fmtInt } from '@/lib/format';
import {
  hasSensorAlert,
  sensorAlertLabel,
  sensorAlertNames,
  simulationLabel,
} from '@/lib/iotPresentation';
import { sensorAverage } from '@/lib/homeSummary';
import type { LoteNave, Nave, RegistroDiario, RegistroDiarioPorLote } from '@/lib/types';
import { useSensoresNave } from '@/lib/queries/iot';
import { SiluetaNave } from './SiluetaNave';
import { applySensorEquipment, getSensorEquipment } from '@/lib/sensorEquipment';

export interface NaveCardProps {
  nave: Nave;
  lote: LoteNave | null;
  registros: RegistroDiario[] | undefined;
  salidaUnidades?: number;
  salidasCargando?: boolean;
  camadaId?: number | null;
  parte?: RegistroDiarioPorLote | null;
  localPending?: boolean;
  canAssign?: boolean;
}

export function NaveCard({
  nave,
  lote,
  registros,
  salidaUnidades = 0,
  salidasCargando = false,
  camadaId = null,
  parte,
  localPending,
  canAssign = true,
}: NaveCardProps) {
  const q = useSensoresNave(nave.id);
  const equipment = getSensorEquipment(nave, q.data);
  const denied = [401, 403].includes((q.error as { status?: number } | null)?.status ?? 0);
  const known = !denied && (q.data != null || nave.sensor_equipment != null);
  const data = known ? applySensorEquipment(q.data, equipment, nave.id) : undefined;
  const installed =
    equipment.environment.length > 0 || equipment.ammonia || equipment.water || equipment.silo;
  const env = data?.environment ?? [];
  const temp = sensorAverage(env.map((e) => e.temperature));
  const hum = sensorAverage(env.map((e) => e.humidity));
  const readings = [
    ...env.flatMap((e) => [e.temperature, e.humidity]),
    data?.ammonia,
    data?.water,
    data?.silo,
  ].filter((r) => r != null);
  const alerts = readings.filter(hasSensorAlert);
  const currentTypes = new Set(
    alerts.filter((r) => r.state === 'alert').map((r) => r.alert_type ?? 'UNKNOWN'),
  );
  const types = new Set(alerts.map((r) => r.alert_type ?? 'UNKNOWN'));
  const alertSummary = Array.from(types).map(
    (type) =>
      (alerts.find((r) => r.state === 'alert' && (r.alert_type ?? 'UNKNOWN') === type) ??
        alerts.find((r) => (r.alert_type ?? 'UNKNOWN') === type))!,
  );
  const disconnected = readings.some((r) => r.state === 'disconnected');
  const provenance = simulationLabel(readings);
  const climateAlert = (kind: 'temperature' | 'humidity') =>
    !data?.local_snapshot && env.some((device) => device[kind]?.state === 'alert');
  const otherAlerts = alertSummary.filter(
    (r) => !env.some((device) => device.temperature === r || device.humidity === r),
  );
  const noData =
    !readings.length || readings.every((r) => r.state === 'no_data' || r.state === 'disconnected');
  const state =
    q.isLoading || (q.isError && !data)
      ? null
      : noData || (data?.local_snapshot && !types.size)
        ? 'gris'
        : currentTypes.size > 1
          ? 'rojo'
          : currentTypes.size
            ? 'amarillo'
            : 'verde';
  const badgeClass =
    state === 'rojo'
      ? 'bg-red-500'
      : state === 'amarillo'
        ? 'bg-warning-text'
        : state === 'gris'
          ? 'bg-text-muted'
          : 'bg-ok-text';
  const badgeLabel = data?.local_snapshot
    ? 'Estado de la última lectura conservada'
    : state === 'rojo'
      ? 'Varias alertas activas'
      : state === 'amarillo'
        ? 'Una alerta activa'
        : state === 'gris'
          ? 'Sin telemetría'
          : 'Sin alertas activas';
  const sensores = env.map((device) => ({
    station: device.station,
    temp: device.temperature?.value ?? null,
    hum: device.humidity?.value ?? null,
    tempState: device.temperature?.state,
    humState: device.humidity?.state,
  }));
  const living =
    !lote || !registros || salidasCargando
      ? null
      : Math.max(
          lote.num_animales_asignados -
            registros.reduce((n, r) => n + r.bajas_muerte + r.bajas_triaje, 0) -
            salidaUnidades,
          0,
        );
  const parteText =
    parte === undefined
      ? 'Parte sin actualizar'
      : parte?.id != null
        ? localPending
          ? 'Parte en el móvil'
          : 'Parte registrado'
        : 'Falta parte';

  if (!lote)
    return (
      <div className="home-empty-nave" data-testid={`nave-card-${nave.codigo}`}>
        <span>{nave.nombre || `Nave ${nave.codigo}`} · Sin animales asignados</span>
        {canAssign && camadaId != null && (
          <Link
            className="ui-text-button"
            to={`/camadas/${camadaId}/asignar-lotes`}
            data-testid={`nave-vacia-cta-${nave.codigo}`}
          >
            Asignar animales
            <ChevronRight size={16} aria-hidden />
          </Link>
        )}
      </div>
    );

  return (
    <Link
      to={`/naves/${nave.id}`}
      className={`home-nave-row${env.length ? '' : ' home-nave-row-no-climate'}`}
      data-testid={`nave-card-${nave.codigo}`}
    >
      <span className="home-nave-drawing" aria-hidden>
        <SiluetaNave mini sensores={sensores} />
      </span>
      <span className="home-nave-copy">
        <strong>
          <span>
            <span className="sr-only">Abrir </span>
            {nave.nombre || `Nave ${nave.codigo}`}
          </span>
          {provenance && (
            <span className="home-nave-demo" title={provenance}>
              <FlaskConical size={12} aria-hidden />
              <span aria-hidden>Demo</span>
              <span className="sr-only">{provenance}</span>
            </span>
          )}
          {otherAlerts.map((reading) => {
            const current = reading.state === 'alert' && !data?.local_snapshot;
            const Icon = reading.alert_type?.startsWith('WATER_')
              ? Droplet
              : reading.alert_type === 'SILO_LOW'
                ? Package
                : reading.alert_type === 'AMMONIA_HIGH'
                  ? Wind
                  : TriangleAlert;
            const label = current
              ? sensorAlertLabel(reading)
              : `Último aviso: ${sensorAlertNames[reading.alert_type ?? ''] ?? 'Alerta de sensor'}${reading.simulated ? ' · Simulada' : ''}`;
            return (
              <span
                key={reading.alert_type}
                className={`home-nave-alert-icon${current ? '' : ' home-nave-alert-icon-retained'}`}
                role="img"
                aria-label={label}
                title={label}
              >
                <Icon size={16} aria-hidden />
                {current ? (
                  <TriangleAlert className="home-nave-alert-marker" size={9} aria-hidden />
                ) : (
                  <Clock3 className="home-nave-alert-marker" size={9} aria-hidden />
                )}
                <span className="sr-only" aria-hidden>
                  {label}
                </span>
              </span>
            );
          })}
        </strong>
        <span className="sr-only">
          {living == null ? 'Saldo no disponible. ' : `${fmtInt(living)} aves. `}
        </span>
        <span className={parte?.id != null ? 'text-ok-text' : 'text-text-secondary'}>
          {parteText}
        </span>
      </span>
      {!!env.length && (
        <span className="home-nave-averages">
          {[
            {
              kind: 'temperature' as const,
              Icon: Thermometer,
              name: 'Temperatura media',
              average: temp,
              unit: '°C',
            },
            {
              kind: 'humidity' as const,
              Icon: Droplet,
              name: 'Humedad media',
              average: hum,
              unit: '%',
            },
          ].map(({ kind, Icon, name, average, unit }) => (
            <span
              key={kind}
              role="group"
              className={`home-nave-metric${climateAlert(kind) ? ' home-nave-metric-alert' : ''}${data?.local_snapshot ? ' text-text-secondary' : ''}`}
              aria-label={`${name}: ${average.value == null ? 'sin lectura' : `${fmt1(average.value)} ${unit}`}${climateAlert(kind) ? '. Alerta en una estación; consultar detalle' : ''}`}
            >
              <Icon size={14} aria-hidden />
              <span aria-hidden>
                {average.value == null ? `— ${unit}` : `${fmt1(average.value)} ${unit}`}
              </span>
              {climateAlert(kind) && <TriangleAlert size={12} aria-hidden />}
            </span>
          ))}
          {((temp.available > 0 && temp.available < temp.total) ||
            (hum.available > 0 && hum.available < hum.total)) && <small>Lecturas parciales</small>}
        </span>
      )}
      <ChevronRight size={14} className="text-text-muted" aria-hidden />
      {state && installed && (
        <span
          className={`sr-only ${badgeClass}`}
          data-testid={`nave-estado-${nave.codigo}`}
          aria-label={badgeLabel}
          aria-hidden
        >
          {badgeLabel}
        </span>
      )}
      <span className="sr-only">
        {alertSummary
          .filter((r) => !otherAlerts.includes(r))
          .map(sensorAlertLabel)
          .join('. ')}
      </span>
      {(q.isError || !known || (installed && (data?.local_snapshot || noData || disconnected))) && (
        <span className="home-nave-notice text-text-secondary">
          {!known && !q.isError ? (
            <LoaderCircle size={13} aria-hidden />
          ) : (
            <CloudOff size={13} aria-hidden />
          )}
          <span aria-hidden>{!known && !q.isError ? 'Consultando…' : 'Sin actualizar'}</span>
          <span className="sr-only">
            {!known && !q.isError
              ? 'Consultando sensores…'
              : data?.local_snapshot
                ? 'Lecturas conservadas · Sin actualización'
                : q.isError
                  ? 'Sensores sin actualizar'
                  : noData
                    ? 'Sin telemetría'
                    : 'Sensores sin conexión'}
          </span>
        </span>
      )}
    </Link>
  );
}
